import React from 'react';

import { usePlanningTasks } from '../PlanningTasks';

import { ActionBarActions } from './ActionBarActions';
import { ActionContinueBtn } from './ActionEntry';

const phases = { design_first_page: 'user_flow', design_remaining: 'first_page', architecture: 'all_pages', pm: 'review_build' } as const;

/** Recover an admitted-later action when its original timeline control is unavailable. */
interface Props {
  projectId: string; sessionId?: string | null; children?: React.ReactNode;
  /** Always-available items for the bar's More menu, shown only when the bar itself shows. */
  menuActions?: React.ReactNode;
  analyticsRef?: React.Ref<HTMLElement>; analyticsScope?: string;
}

export const PendingDesignAction: React.FC<Props> = ({
  projectId, sessionId, children, menuActions, analyticsRef, analyticsScope,
}) => {
  const { tasks } = usePlanningTasks(projectId);
  // 'cancelled' is here because a stage that died before starting its children
  // now lands there rather than in 'error'. Without it this component renders
  // nothing and the run loses its only route back.
  const aborted = (status: string) => status === 'error' || status === 'cancelled';
  const pending = tasks.find((task) => task.task_type === 'planning'
    && (task.status === 'backlog' || aborted(task.status))
    && task.planning_metadata?.artifact_type === 'stage'
    && (task.planning_metadata?.dispatch_admission_pending === true || (aborted(task.status)
      && ['design_first_page', 'design_remaining'].includes(task.planning_metadata?.stage ?? '')))
    && task.planning_metadata?.active_selection_invocation_id
    && typeof task.planning_metadata.stage === 'string' && task.planning_metadata.stage in phases);
  const running = tasks.some((task) => ['queued', 'ai_in_progress'].includes(task.status)
    && task.planning_metadata?.stage === pending?.planning_metadata?.stage);
  const metadata = pending?.planning_metadata;
  const invocation = metadata?.active_selection_invocation_id;
  const selections = metadata?.action_selections as Record<string, { displayed_label?: unknown }> | undefined;
  const saved = typeof invocation === 'string' ? selections?.[invocation] : undefined;

  if (running || !pending || typeof invocation !== 'string' || !saved || !metadata) {
    return children ? <section ref={analyticsRef} data-action-analytics-scope={analyticsScope}
      className="sticky-planning-actions ai-overview-action-bar notranslate"
      data-sticky-action-mirror="true" role="group" aria-label="Project actions">
      <ActionBarActions>{children}{menuActions}</ActionBarActions>
    </section> : null;
  }
  const phase = phases[metadata.stage as keyof typeof phases];
  const label = aborted(pending.status) ? 'Retry Failed Pages'
    : typeof saved.displayed_label === 'string' ? saved.displayed_label : 'Continue';

  return <section ref={analyticsRef} data-action-analytics-scope={analyticsScope}
    data-source-entry-id={invocation} className="sticky-planning-actions ai-overview-action-bar notranslate"
    data-sticky-action-mirror="true" role="group" aria-label="Pending page generation">
    <ActionBarActions>
      {children}
      {menuActions}
      <ActionContinueBtn key={invocation} entryId={invocation} phase={phase}
        projectId={projectId} sessionId={sessionId} isLast
        button={{ key: 'continue', kind: 'workflow', action: 'resume_design', label }} />
    </ActionBarActions>
  </section>;
};
